interface PageResponse<T> {
  content?: T[]
  last?: boolean
  /** Read where the endpoint states its page count instead of `last` (e.g. `listUsers`). */
  totalPages?: number
}

interface FetchAllPagesOptions {
  pageSize?: number
  maxPages?: number
}

const DEFAULT_PAGE_SIZE = 100
const DEFAULT_MAX_PAGES = 50

/**
 * Fetch all pages of a paginated endpoint and return the concatenated content.
 *
 * The fetcher receives `(page, size)` and must return a Spring-style page
 * response with `content` and either `last` or `totalPages`. Iteration stops
 * when `last` is true, when the last of `totalPages` has been read, or when
 * `maxPages` is reached. The cap prevents an infinite loop if a backend
 * bug ever returns `last: false` indefinitely; hitting it logs a warning.
 */
export async function fetchAllPages<T>(
  fetcher: (page: number, size: number) => Promise<{ data: PageResponse<T> }>,
  options: FetchAllPagesOptions = {}
): Promise<T[]> {
  const pageSize = options.pageSize ?? DEFAULT_PAGE_SIZE
  const maxPages = options.maxPages ?? DEFAULT_MAX_PAGES
  const items: T[] = []

  for (let page = 0; page < maxPages; page++) {
    const response = await fetcher(page, pageSize)
    items.push(...(response.data.content ?? []))
    const { last, totalPages } = response.data
    if (last || (totalPages !== undefined && page + 1 >= totalPages)) {
      return items
    }
  }

  console.warn(
    `fetchAllPages: hit max page cap (${maxPages} pages of ${pageSize}); results may be incomplete`
  )
  return items
}
